/*
  50 Grams product gallery (<g50-gallery>), ported from the prototype (js/components.js gallery,
  v3/v2.js dots). Markup: snippets/g50-product-gallery.liquid.
  - Thumbnails pick the main image; the strip pages with the arrows and keeps the picked one in view.
  - Phones: swipe the main image; dots (or thumbnails) follow it.
  - Desktop: hover zoom after a short delay, following the pointer.
  - Click opens a full screen view; Esc, the close button and the browser Back button close it.
  Horizon replaces the whole gallery on a variant change, so everything is set up per element
  and torn down when it leaves the page.
*/
(function () {
  "use strict";

  if (customElements.get("g50-gallery")) return;

  const reduce = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  class G50Gallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const signal = this.abort.signal;

      const track = this.querySelector(".gallery__track");
      const slides = [...this.querySelectorAll(".gallery__slide")];
      const thumbs = [...this.querySelectorAll(".gallery__thumb")];
      const strip = this.querySelector(".gallery__thumbs");
      const prev = this.querySelector("[data-thumbs-prev]");
      const next = this.querySelector("[data-thumbs-next]");
      const dots = this.querySelector("[data-gallery-dots]");
      const overlay = this.querySelector("dialog.gallery-overlay");
      if (!track || !slides.length) return;

      let index = 0;
      let programmatic = null;
      let revealing = false;

      // Load every image once the shopper shows interest, so a picked image is ready, not blank
      const eager = () => this.querySelectorAll("img[loading='lazy']").forEach((img) => (img.loading = "eager"));
      this.addEventListener("pointerenter", eager, { once: true, signal });
      this.addEventListener("focusin", eager, { once: true, signal });
      track.addEventListener("touchstart", eager, { once: true, passive: true, signal });

      // Built fresh each time: the element can be moved, which runs this again
      const dotButtons = [];
      if (dots) dots.replaceChildren();
      if (dots && thumbs.length) {
        thumbs.forEach((thumb, n) => {
          const b = document.createElement("button");
          b.type = "button";
          b.setAttribute("aria-label", `Show image ${n + 1} of ${thumbs.length}`);
          b.addEventListener("click", () => show(n));
          dots.append(b);
          dotButtons.push(b);
        });
      }

      const mark = () => {
        thumbs.forEach((t, n) => t.setAttribute("aria-current", n === index ? "true" : "false"));
        dotButtons.forEach((d, n) => d.setAttribute("aria-current", n === index ? "true" : "false"));
      };

      // Keep the selected thumbnail in view: align it to the start of the strip, snap keeps whole thumbnails
      const revealThumb = (i, smooth = !reduce()) => {
        if (!strip || !thumbs[i] || !strip.clientWidth) return;
        const t = thumbs[i].getBoundingClientRect();
        const s = strip.getBoundingClientRect();
        if (t.left < s.left - 1 || t.right > s.right + 1) {
          revealing = smooth;
          strip.scrollTo({ left: strip.scrollLeft + (t.left - s.left), behavior: smooth ? "smooth" : "auto" });
        }
      };

      // If a smooth reveal was interrupted, snap the selected thumbnail into view when scrolling ends
      if (strip) {
        strip.addEventListener("scrollend", () => {
          if (!revealing) return;
          revealing = false;
          revealThumb(index, false);
        }, { signal });
      }

      const syncArrows = () => {
        if (!strip || !prev || !next) return;
        prev.disabled = strip.scrollLeft <= 1;
        next.disabled = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
      };

      const show = (i, smooth = true) => {
        index = (i + slides.length) % slides.length;
        // Ignore the track's own scroll events while it animates to the chosen image
        clearTimeout(programmatic);
        programmatic = setTimeout(() => (programmatic = null), 900);
        track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: smooth && !reduce() ? "smooth" : "auto" });
        mark();
        revealThumb(index);
      };
      this.show = show;

      // Keep the chosen image aligned if the layout shifts (fonts loading, resize)
      const realign = () => {
        if (programmatic) return;
        track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: "auto" });
      };
      window.addEventListener("resize", realign, { signal });
      if (document.fonts) document.fonts.ready.then(() => this.isConnected && realign());

      thumbs.forEach((t, n) => t.addEventListener("click", () => show(n), { signal }));

      if (strip && prev && next) {
        const page = (dir) => strip.scrollBy({ left: dir * (strip.clientWidth + 8), behavior: reduce() ? "auto" : "smooth" });
        prev.addEventListener("click", () => page(-1), { signal });
        next.addEventListener("click", () => page(1), { signal });
        strip.addEventListener("scroll", () => requestAnimationFrame(syncArrows), { signal });
        window.addEventListener("resize", syncArrows, { signal });
        syncArrows();
      }

      // Swipe on phones: keep thumbnails and dots in sync with the visible slide
      let raf;
      track.addEventListener("scroll", () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          if (programmatic || !track.clientWidth) return;
          const i = Math.round(track.scrollLeft / track.clientWidth);
          if (i !== index && slides[i]) {
            index = i;
            mark();
            revealThumb(index);
          }
        });
      }, { signal });

      const zoom = this.hasAttribute("data-zoom");

      // Hover zoom on desktop, after a short delay
      if (zoom) {
        const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
        slides.forEach((slide) => {
          if (!slide.matches("button")) return;
          let timer;
          slide.addEventListener("pointerenter", () => {
            if (!fine.matches || !slide.querySelector("img")) return;
            const delay = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--g50-duration-zoom-delay")) || 300;
            timer = setTimeout(() => slide.classList.add("is-zoomed"), delay);
          }, { signal });
          slide.addEventListener("pointermove", (e) => {
            const r = slide.getBoundingClientRect();
            slide.style.setProperty("--zoom-x", ((e.clientX - r.left) / r.width) * 100 + "%");
            slide.style.setProperty("--zoom-y", ((e.clientY - r.top) / r.height) * 100 + "%");
          }, { signal });
          slide.addEventListener("pointerleave", () => {
            clearTimeout(timer);
            slide.classList.remove("is-zoomed");
          }, { signal });
        });
      }

      // Full screen view: Esc (native dialog), the close button and Back all close it
      if (zoom && overlay) {
        const stage = overlay.querySelector(".gallery-overlay__stage");
        const title = overlay.querySelector("[data-overlay-title]");
        let opener = null;
        let pushed = false;

        const render = () => {
          const slide = slides[index];
          const img = slide.querySelector("img");
          const big = document.createElement("img");
          big.src = slide.dataset.full || (img && img.currentSrc) || "";
          big.alt = img ? img.alt : "";
          stage.replaceChildren(big);
          title.textContent = slide.getAttribute("aria-label") || "";
        };

        const close = () => {
          if (overlay.open) overlay.close();
        };

        // Back closes the view: one history entry while it is open
        const onPop = () => {
          if (!pushed) return;
          pushed = false;
          close();
        };
        window.addEventListener("popstate", onPop, { signal });

        const requestClose = () => {
          if (pushed) history.back();
          else close();
        };

        slides.forEach((slide) => {
          if (!slide.matches("button")) return;
          slide.addEventListener("click", () => {
            slide.classList.remove("is-zoomed");
            index = slides.indexOf(slide);
            opener = slide;
            render();
            overlay.showModal();
            history.pushState({ g50Gallery: true }, "");
            pushed = true;
          }, { signal });
        });

        overlay.addEventListener("cancel", (e) => {
          e.preventDefault();
          requestClose();
        }, { signal });
        overlay.addEventListener("close", () => {
          if (opener) opener.focus({ preventScroll: true });
        }, { signal });
        overlay.addEventListener("click", (e) => {
          if (e.target === overlay) requestClose();
        }, { signal });
        overlay.querySelector("[data-overlay-close]").addEventListener("click", requestClose, { signal });

        const step = (dir) => {
          show(index + dir, false);
          render();
        };
        overlay.querySelector("[data-overlay-prev]")?.addEventListener("click", () => step(-1), { signal });
        overlay.querySelector("[data-overlay-next]")?.addEventListener("click", () => step(1), { signal });
        overlay.addEventListener("keydown", (e) => {
          if (e.key === "ArrowLeft") step(-1);
          if (e.key === "ArrowRight") step(1);
        }, { signal });

        this.closeOverlay = () => {
          if (pushed) {
            pushed = false;
            history.back();
          }
          close();
        };
      }

      mark();
    }

    disconnectedCallback() {
      // A variant change replaces the gallery; drop the history entry if the view was open
      if (this.closeOverlay) this.closeOverlay();
      if (this.abort) this.abort.abort();
      this.ready = false;
    }
  }

  customElements.define("g50-gallery", G50Gallery);
})();
